import { HttpClient } from '@angular/common/http';
import { computed, effect, Inject, inject, Injectable, signal } from '@angular/core';
import { AbstractControl, AsyncValidatorFn, FormArray, FormControl, FormGroup, ValidationErrors } from '@angular/forms';
import { plainToClass } from 'class-transformer';
import { catchError, debounceTime, distinctUntilChanged, map, Observable, of, startWith, Subscription, switchMap, take } from 'rxjs';

import {
  AiChatState,
  IFollowupQuestionRequest,
  InputType,
  Question as BaseQuestion,
  QuestionType,
  SurveyResult,
  SurveyAutomaticAnswers
} from '@empathy/survey/base';
import { Question as CoreQuestion } from '@empathy/survey-core';

import { SurveyAnswerParserService } from './survey-answer-parser.service';
import { SurveyConditionalService } from './survey-conditional.service';
import { EnvironmentConfig } from '../config/environment.config';
import { AiChatAudioUploadResponse } from '../models/ai-chat-audio-upload-response.interface';
import { QuestionControl } from '../models/question-control.interface';

@Injectable({ providedIn: 'root' })
export class SurveyViewerService {
  answerId$ = signal<string>(null);
  version$ = signal<number>(0);
  activePageIndex$ = signal(-1);
  canGoBackInd$ = signal(false);
  inviteId$ = signal(null);
  isTestMode$ = signal(false);
  isBackButtonDisabled$ = signal(false);
  progress$ = signal(0);

  notAllCtrlsValid$ = signal(false);
  questionControls$ = signal<QuestionControl>({});
  automaticQuestionIds$ = signal<Set<string>>(new Set());
  visibleQuestions$ = computed(() => Object.values(this.questions$()).filter((q) => q.visible && !this.automaticQuestionIds$().has(q.id)));
  readonly pipingQuestions$ = computed(() => this.questions$());

  private questions$ = signal<Record<string, BaseQuestion>>({});
  private readonly automaticAnswers = new SurveyAutomaticAnswers();

  private readonly conditionalRunDebounceMs = 500;
  private subscriptions: Subscription[] = [];

  private conditionalService = inject(SurveyConditionalService);
  private http = inject(HttpClient);
  private surveyAnswerParserService = inject(SurveyAnswerParserService);

  constructor(@Inject('environment') private environment: EnvironmentConfig) {
    effect(
      () => {
        // TODO: Refactor
        if (this.questionControls$()) {
          if (this.subscriptions.length > 0) {
            this.subscriptions.forEach((sub) => sub.unsubscribe());
            this.subscriptions = [];
          }
          this.notAllCtrlsValid$.set(Object.values(this.questionControls$()).some((ctrl) => ctrl.invalid || ctrl.pending));
          Object.values(this.questionControls$()).forEach((control) => {
            this.subscriptions.push(
              control.valueChanges.subscribe(() => {
                this.notAllCtrlsValid$.set(Object.values(this.questionControls$()).some((ctrl) => ctrl.invalid || ctrl.pending));
              })
            );

            this.subscriptions.push(
              control.statusChanges.subscribe((status) => {
                if (status === 'PENDING') {
                  this.notAllCtrlsValid$.set(true);
                } else {
                  this.notAllCtrlsValid$.set(Object.values(this.questionControls$()).some((ctrl) => ctrl.invalid || ctrl.pending));
                }
              })
            );
          });
        }
      },
      { allowSignalWrites: true }
    );
  }

  mapToQuestionControlsAndResume(questions: CoreQuestion[], answers: SurveyResult, options: { previewMode?: boolean } = {}): void {
    this.automaticQuestionIds$.set(new Set());
    this.questionControls$.set({});
    this.mapToQuestionControls(questions, options);
    const nonAiChatAnswers = this.excludeAiChatAnswers(answers);
    this.mapConditionality(questions, nonAiChatAnswers);
    this.mapAnswerToQuestionControl(nonAiChatAnswers, { emitEvent: false });
    this.conditionalService.runAll(
      questions.map((question) => question.id),
      { skipReset: true }
    );
    this.questions$.set({ ...this.conditionalService.questions });
    this.populateAutomaticAnswers(questions);
  }

  mapSubmittedQuestionIds(): string[] {
    return Object.keys(this.questionControls$()).filter((qId) => this.questionControls$()[qId].enabled);
  }

  mapAnswers(): Record<string, string> {
    return this.mapSubmittedQuestionIds()
      .filter((qId) => this.questions$()[qId]?.type !== QuestionType.AIChat)
      .reduce(
        (results, qId) => {
          results[qId] = JSON.stringify(this.questionControls$()[qId].value);
          return results;
        },
        {} as Record<string, string>
      );
  }

  destroy(): void {
    this.questionControls$.set({});
    this.questions$.set({});
  }

  aiFollowupCall(request: IFollowupQuestionRequest): Observable<AiChatState> {
    return this.http.post<AiChatState>(`${this.environment.surveyEndPoint}${this.inviteId$()}/followup`, request);
  }

  uploadAiChatAudio(questionId: string, turnId: string, audio: Blob): Observable<AiChatAudioUploadResponse> {
    const formData = new FormData();
    formData.append('audio', audio);
    formData.append('turnId', turnId);

    return this.http.post<AiChatAudioUploadResponse>(this.aiChatAudioUrl(questionId), formData);
  }

  downloadAiChatAudio(questionId: string, mediaId: string): Observable<Blob> {
    return this.http.get(this.aiChatAudioUrl(questionId, mediaId), { responseType: 'blob' });
  }

  private populateAutomaticAnswers(questions: CoreQuestion[]): void {
    const automaticIds = new Set(this.automaticQuestionIds$());
    for (let pass = 0; pass <= questions.length; pass++) {
      const visibleQuestions = Object.values(this.questions$()).filter((q) => q.visible !== false && !q.neverShow && this.questionControls$()[q.id].enabled);
      // Eligibility must be checked independently of whether an answer already exists.
      const eligibleCandidates = this.automaticAnswers.getCandidates(visibleQuestions, {});
      const eligibleAnswers = new Map(eligibleCandidates.map((candidate) => [candidate.question.id, candidate.answerJson]));
      let invalidated = false;
      for (const id of automaticIds) {
        if (eligibleAnswers.get(id) !== JSON.stringify(this.questionControls$()[id].getRawValue())) {
          automaticIds.delete(id);
          this.questionControls$()[id].reset(undefined, { emitEvent: false });
          this.conditionalService.answers[id] = this.questionControls$()[id].value;
          invalidated = true;
        }
      }

      const candidates = this.automaticAnswers
        .getCandidates(
          eligibleCandidates.map((candidate) => candidate.question),
          Object.fromEntries(
            eligibleCandidates.map((candidate) => [candidate.question.id, JSON.stringify(this.questionControls$()[candidate.question.id].value)])
          )
        )
        // Empty carousel answers can remain empty after generation; do not repeat a no-op write.
        .filter((candidate) => candidate.answerJson !== JSON.stringify(this.questionControls$()[candidate.question.id].getRawValue()));
      if (!candidates.length && !invalidated) {
        break;
      }
      for (const candidate of candidates) {
        automaticIds.add(candidate.question.id);
        const value = this.surveyAnswerParserService.parseAnswerValue(candidate.answerJson);
        this.questionControls$()[candidate.question.id].patchValue(value, { emitEvent: false });
        this.conditionalService.answers[candidate.question.id] = value as SurveyResult[string];
      }
      // Generated answers must invalidate downstream choices just like respondent answers.
      this.conditionalService.runAll(questions.map((q) => q.id));
      this.questions$.set({ ...this.conditionalService.questions });
    }
    // Recheck eligibility after the final conditional pass. OTHER text may still need respondent input.
    const finalEligibleAnswers = new Map(
      this.automaticAnswers
        .getCandidates(
          Object.values(this.questions$()).filter((q) => q.visible !== false && !q.neverShow && this.questionControls$()[q.id].enabled),
          {}
        )
        .map((candidate) => [candidate.question.id, candidate.answerJson])
    );
    const nextAutomaticIds = new Set(
      [...automaticIds].filter(
        (id) => finalEligibleAnswers.get(id) === JSON.stringify(this.questionControls$()[id].getRawValue()) && this.questionControls$()[id].valid
      )
    );
    const previousAutomaticIds = this.automaticQuestionIds$();
    if (nextAutomaticIds.size !== previousAutomaticIds.size || [...nextAutomaticIds].some((id) => !previousAutomaticIds.has(id))) {
      this.automaticQuestionIds$.set(nextAutomaticIds);
    }
    this.notAllCtrlsValid$.set(Object.values(this.questionControls$()).some((ctrl) => ctrl.invalid || ctrl.pending));
  }

  private aiChatAudioUrl(questionId: string, audioId?: string): string {
    const baseUrl = `${this.environment.surveyEndPoint}${this.inviteId$()}/ai-chat/${encodeURIComponent(questionId)}/audio`;
    return audioId ? `${baseUrl}/${encodeURIComponent(audioId)}` : baseUrl;
  }

  private mapToQuestionControls(questions: CoreQuestion[], options: { previewMode?: boolean }): void {
    const mappedQuestions: Record<string, BaseQuestion> = {};

    this.questionControls$.set(
      questions
        .map((q) => plainToClass(CoreQuestion, q))
        .reduce((controls, question) => {
          mappedQuestions[question.id] = question;
          controls[question.id] = question.type === QuestionType.AIChat ? new FormControl(null) : question.getFormControl();
          if (!options.previewMode && question.monitorTerms?.monitorProblematicInd) {
            this.mapQuestionAsyncValidators(question, controls[question.id]);
          }
          return controls;
        }, {} as QuestionControl)
    );

    this.questions$.set(mappedQuestions);
  }

  private mapConditionality(questions: CoreQuestion[], answers: SurveyResult): void {
    this.conditionalService.destroy();

    questions.forEach((question) => {
      if (question.type !== QuestionType.AIChat) {
        this.questionControls$()
          [question.id].valueChanges.pipe(debounceTime(this.conditionalRunDebounceMs), distinctUntilChanged())
          .subscribe(() => {
            this.conditionalService.answers[question.id] = this.questionControls$()[question.id].value;
            this.conditionalService.run(question.id);

            this.questions$.set({ ...this.conditionalService.questions });
            this.populateAutomaticAnswers(questions);
          });
      }

      this.conditionalService.mapConditionality(question.id, this.questions$(), this.questionControls$(), answers);
    });
  }

  private excludeAiChatAnswers(answers: SurveyResult): SurveyResult {
    return Object.entries(answers ?? {}).reduce((clientAnswers, [questionId, answer]) => {
      if (this.questions$()[questionId]?.type !== QuestionType.AIChat) {
        clientAnswers[questionId] = answer;
      }
      return clientAnswers;
    }, {} as SurveyResult);
  }

  private mapAnswerToQuestionControl(answers: SurveyResult, options?: { emitEvent?: boolean }): void {
    for (const qId in answers) {
      if (Object.prototype.hasOwnProperty.call(answers, qId)) {
        const qControl = this.questionControls$()[qId];
        if (qControl) {
          qControl.patchValue(this.surveyAnswerParserService.parseAnswerValue(answers[qId]), {
            emitEvent: options?.emitEvent ?? true
          });
        }
      }
    }
  }

  private mapQuestionAsyncValidators(question: CoreQuestion, qControl: FormControl<any> | FormArray<any> | FormGroup<any>): void {
    switch (question.type) {
      case QuestionType.Input: {
        if (question.inputType === InputType.Text) {
          qControl.addAsyncValidators(this.validateBlockedTerms(question.id));
        }
        break;
      }
      case QuestionType.Essay: {
        qControl.addAsyncValidators(this.validateBlockedTerms(question.id));
        break;
      }

      default:
        return;
    }
  }

  private validateBlockedTerms(id: string): AsyncValidatorFn {
    const minCharactersNeeded = 3;

    return (control: AbstractControl): Observable<ValidationErrors | null> => {
      if (!control.value) {
        return of(null);
      }

      if (control.value.length < minCharactersNeeded) {
        return of({
          message: `Input must be at least ${minCharactersNeeded} characters long.`,
          isCustom: true
        });
      }

      return control.valueChanges.pipe(
        startWith(control.value),
        debounceTime(2000),
        distinctUntilChanged(),
        switchMap((value: string) => {
          if (!value || value.length < minCharactersNeeded) {
            return of(null);
          }

          return this.isBlocked(id, this.mapAnswers()).pipe(
            map((response) =>
              response?.isBlocked
                ? { unique: true, message: response.errorMessage || 'Please review your input and try again before continuing.', isCustom: true }
                : null
            ),
            catchError(() => of(null))
          );
        }),
        take(1)
      );
    };
  }

  private isBlocked(questionId: string, answers: SurveyResult): Observable<{ isBlocked: boolean; errorMessage: string } | null> {
    // TODO: Why this is sending the whole answers object instead of just the answer for the question being monitored?
    return this.http
      .post<{
        isBlocked: boolean;
        errorMessage: string;
      }>(`${this.environment.surveyEndPoint}${this.inviteId$()}/block-terms`, { questionId, answers })
      .pipe(catchError(() => of(null)));
  }
}
